import { User } from '@firebase/auth';
import { Auth, User as FirebaseUser, onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';
import React, { createContext, useEffect, useState } from 'react';

import { IGroup } from '@customTypes/group';
import { IUser } from '@customTypes/user';
import { auth, db } from 'external/firebase';
import { getGroup } from 'external/firebase/firestore/collections/groups';
import { getUserProfile } from 'external/firebase/firestore/collections/users';

import useLocalStorage from 'hooks/useLocalStorage';

interface UserState {
    isInitialized: boolean;
    user: FirebaseUser | null;
    profile?: IUser;
    selectedGroup?: IGroup;
    onChangeSelectedGroup: (group: string | undefined) => void;
}

const initialUserState: UserState = {
    isInitialized: false,
    user: null,
    profile: undefined,
    selectedGroup: undefined,
    onChangeSelectedGroup: (group: string | undefined) => {},
};

const AuthContext = createContext(initialUserState);

const AuthProvider: React.FunctionComponent = ({ children }) => {
    const [userState, setUserState] = useState(initialUserState);

    const [persistentContext, setPersistentContext] = useLocalStorage('persistent-context', {
        selectedGroup: initialUserState.selectedGroup,
    });

    const onChangeSelectedGroup = async (groupId: string | undefined) => {
        if (!groupId) {
            setPersistentContext({ selectedGroup: undefined });
            return;
        }
        try {
            const group = await getGroup(groupId);
            setPersistentContext({ selectedGroup: group });
        } catch (error) {
            console.error(error);
        }
    };

    const handleStateChange = async (user: User | null) => {
        if (user) {
            // The cached Firebase User object can hold a stale `emailVerified`
            // value: when a user verifies their email in another tab/session the
            // current session's token is not refreshed automatically. Without
            // this reload the AuthGuard keeps bouncing already-verified
            // children/parents to the verification step. reload() refreshes
            // emailVerified on the live user object (getIdToken alone does not).
            if (!user.emailVerified) {
                try {
                    await user.reload();
                } catch (error) {
                    console.error('Error reloading user:', error);
                }
            }

            // Set up Firestore onSnapshot listener
            const userDocRef = doc(db, 'users', user.uid);
            const unsubscribe = onSnapshot(userDocRef, async docSnapshot => {
                if (docSnapshot.exists()) {
                    const userProfile = docSnapshot.data() as Omit<IUser, 'id'>;
                    setUserState(prevState => ({
                        ...prevState,
                        isInitialized: true,
                        user,
                        profile: {
                            ...userProfile,
                            id: docSnapshot.id,
                        },
                    }));

                    if (userProfile.emailVerified !== user.emailVerified && userProfile.role !== 'teacher') {
                        try {
                            await updateDoc(userDocRef, {
                                emailVerified: user.emailVerified,
                            });
                        } catch (error) {
                            console.error('Error updating user emailVerified status:', error);
                        }
                    }
                } else {
                    setUserState(prevState => ({
                        ...prevState,
                        isInitialized: true,
                        user,
                        profile: undefined,
                    }));
                }
            });

            // Pendo user identity registration
            // @ts-ignore
            pendo.initialize({
                visitor: {
                    id: user.uid,
                    email: user.email,
                    full_name: user.displayName,
                    role: userState.profile?.role,
                    language: 'lt',

                    // You can add any additional visitor level key-values here,
                    // as long as it's not one of the above reserved names.
                },

                account: {
                    id: user.uid,
                    // name:         // Optional
                    // is_paying:    // Recommended if using Pendo Feedback
                    // monthly_value:// Recommended if using Pendo Feedback
                    // planLevel:    // Optional
                    // planPrice:    // Optional
                    // creationDate: // Optional

                    // You can add any additional account level key-values here,
                    // as long as it's not one of the above reserved names.
                },
            });

            // Cleanup the Firestore listener when the user logs out or component unmounts
            return () => {
                unsubscribe();
            };
        }
        setUserState({
            ...userState,
            isInitialized: true,
            user: null,
            profile: undefined,
            selectedGroup: undefined,
        });
    };

    useEffect(() => {
        const unsubscribeAuth = onAuthStateChanged(auth, handleStateChange);

        // Cleanup function
        return () => {
            unsubscribeAuth();
        };
    }, [auth]);

    return (
        <AuthContext.Provider
            value={{ ...userState, selectedGroup: persistentContext.selectedGroup, onChangeSelectedGroup }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export { AuthContext, AuthProvider };
